import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Spinner, Table, Badge } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";

const OperationsOverview = () => {
 const { search } = useLocation();
 const workOrderId = new URLSearchParams(search).get("workOrder");

 const [loading, setLoading] = useState(true);
 const [workOrder, setWorkOrder] = useState(null);
 const [documentsByType, setDocumentsByType] = useState({});
 const [gantt, setGantt] = useState(null);
 const [dpr, setDpr] = useState([]);
 const [metadata, setMetadata] = useState({});

 /* =============================
 FETCH OPERATIONS OVERVIEW
 ============================= */
 useEffect(() => {
 if (!workOrderId) return;

 const load = async () => {
 try {
 setLoading(true);

 const res = await api.get(
 `/operations/overview?workOrder=${workOrderId}`
 );

 if (res.data.success) {
 const data = res.data.data;
 setWorkOrder(data.workOrder);
 setDocumentsByType(data.documentsByType || {});
 setGantt(data.gantt || null);
 setDpr(data.dpr || []);
 setMetadata(data.metadata || {});
 }
 } catch (err) {
 console.error("Operations overview error:", err);
 } finally {
 setLoading(false);
 }
 };

 load();
 }, [workOrderId]);

 /* =============================
 DOCUMENT SUMMARY
 ============================= */
 const documentSummary = useMemo(() => {
 const result = {};
 Object.keys(documentsByType).forEach((type) => {
 result[type] = documentsByType[type]?.length || 0;
 });
 return result;
 }, [documentsByType]);

 if (!workOrderId) {
 return (
 <div className="text-center py-5 text-muted">
 Please open this page from Operations
 </div>
 );
 }

 if (loading) {
 return (
 <div className="text-center py-5">
 <Spinner />
 </div>
 );
 }

 return (
 <>
 <PageTitle
 activeMenu="Operations Overview"
 motherMenu="Commercial"
 />

 {/* ================= HEADER CARD (CONTEXT) ================= */}
 {metadata.workOrder && (
 <Card className="mb-4 shadow-sm">
 <Card.Body>
 <h5 className="fw-bold mb-1">{metadata.workOrder.woNo}</h5>
 <small className="text-muted">
 {metadata.workOrder.projectName || "Operations Overview"}
 </small>
 </Card.Body>
 </Card>
 )}

 {/* ================= DOCUMENT SUMMARY ================= */}
 <Row className="g-3 mb-4">
 {Object.keys(documentSummary).map((type) => (
 <Col md={3} key={type}>
 <Card className="text-center shadow-sm">
 <Card.Body>
 <small className="text-muted">{type}</small>
 <h4 className="fw-bold mt-1">
 {documentSummary[type]}
 </h4>
 </Card.Body>
 </Card>
 </Col>
 ))}
 </Row>

 {/* ================= DOCUMENT DETAILS ================= */}
 <Card className="shadow-sm mb-4">
 <Card.Body>
 <h6 className="fw-bold mb-3">Documents</h6>

 {Object.keys(documentsByType).length === 0 ? (
 <p className="text-muted">No documents uploaded</p>
 ) : (
 Object.entries(documentsByType).map(
 ([type, docs]) => (
 <div key={type} className="mb-4">
 <h6 className="fw-semibold">
 {type}{" "}
 <Badge bg="secondary">{docs.length}</Badge>
 </h6>

 {docs.length === 0 ? (
 <p className="text-muted small">
 No documents
 </p>
 ) : (
 <Table
 bordered
 hover
 size="sm"
 className="mt-2"
 >
 <thead>
 <tr>
 <th>#</th>
 <th>Title</th>
 <th>Date</th>
 </tr>
 </thead>
 <tbody>
 {docs.map((doc, idx) => (
 <tr key={doc._id}>
 <td>{idx + 1}</td>
 <td>
 {doc.title ||
 doc.fileName ||
 "—"}
 </td>
 <td>
 {doc.createdAt
 ? new Date(
 doc.createdAt
 ).toLocaleDateString()
 : "—"}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </div>
 )
 )
 )}
 </Card.Body>
 </Card>

 {/* ================= GANTT ================= */}
 <Card className="shadow-sm mb-4">
 <Card.Body>
 <h6 className="fw-bold mb-2">Gantt Chart</h6>
 {gantt ? (
 <Badge bg="success">Available</Badge>
 ) : (
 <Badge bg="secondary">Not Created</Badge>
 )}
 </Card.Body>
 </Card>

 {/* ================= DPR ================= */}
 <Card className="shadow-sm">
 <Card.Body>
 <h6 className="fw-bold mb-3">DPR</h6>

 {dpr.length === 0 ? (
 <p className="text-muted">No DPR records</p>
 ) : (
 <Table bordered hover size="sm">
 <thead>
 <tr>
 <th>#</th>
 <th>Date</th>
 <th>Remarks</th>
 </tr>
 </thead>
 <tbody>
 {dpr.map((d, idx) => (
 <tr key={d._id}>
 <td>{idx + 1}</td>
 <td>
 {d.date
 ? new Date(d.date).toLocaleDateString()
 : "—"}
 </td>
 <td>{d.remarks || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Card.Body>
 </Card>
 </>
 );
};

export default OperationsOverview;
